import {
  filtersToGraphQLFilters,
  IPageFilterProps,
  IProperty,
  makeDynamicEntitiesPageStore,
  PageFiltersRenderer,
  PerformanceEntitiesKey,
  performanceEntityFilterOptionsQuery,
  SortModel,
  useParams,
  PEOPLE_FLOW_ID,
} from '@diana/core';
import { useQuery } from '@tanstack/react-query';
import { omit } from 'lodash';
import { ReactElement } from 'react';

export const ThingsFilterRenderer = ({
  filterProps,
  peoplePage,
}: {
  filterProps: IPageFilterProps;
  peoplePage: boolean;
}) => {
  const { property } = filterProps.filter;
  const { flowOriginId } = useParams<{ flowOriginId: string }>();
  const useEntitiesPageStore = makeDynamicEntitiesPageStore(flowOriginId || PEOPLE_FLOW_ID);
  const entitiesLength = useEntitiesPageStore((store) => store.entities?.length);
  const flowType = useEntitiesPageStore((store) => store.flow?.flowType);
  const flowId = useEntitiesPageStore((store) => store.flow?.id);
  const flowOriginIds = useEntitiesPageStore((store) => store.flow?.flowOriginIds ?? []);
  const availableFilters = useEntitiesPageStore((store) => store.availableFilters);

  const filterProperties = filtersToGraphQLFilters({
    filters: availableFilters,
    filtersValues: omit(filterProps.filtersValues, property.key), // prevent passing filtered values for the the said current filter key
  });

  const optionsQuery = performanceEntityFilterOptionsQuery.get({
    key: property.key,
    displayNamePath: property.displayNamePath,
    flowTypes: [flowType],
    isDefaultProperty: property.isDefaultProperty,
    valuePaths: property.valuePaths,
    flowIds: [flowId],
    flowOriginIds: peoplePage ? flowOriginIds : [flowOriginId!],
    showCompleted: true,
    showDeleted: false,
    showInProgress: true,
    filterType: property.type!,
    filterProperties,
  });

  const { data, isLoading } = useQuery({
    queryKey: [...optionsQuery.key, flowType, flowId, entitiesLength, filterProperties],
    queryFn: optionsQuery.fn,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  return <PageFiltersRenderer filterProps={filterProps} data={data} isLoading={isLoading} />;
};

export const createPropertyToFilter = (peoplePage: boolean) => {
  return (property: IProperty) => {
    return {
      property,
      renderFilter: (props: IPageFilterProps): ReactElement => {
        return <ThingsFilterRenderer filterProps={props} peoplePage={peoplePage} />;
      },
    };
  };
};

export const getThingsGraphQLWorksQueryKey = (
  flowId: string,
  graphQLFilters: string,
  sortModel?: SortModel,
): string[] => [PerformanceEntitiesKey, flowId, graphQLFilters, JSON.stringify(sortModel)];
